import React from "react";
import { Box, Typography, Divider, Alert } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { StudentResponseDto } from "../../shared/dtos/student/StudentResponseDto";
import { formatBloodGroup } from "../../helpers/enumHelpers";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface ViewStudentModalProps {
  open: boolean;
  onClose: () => void;
  student: StudentResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewStudentModal: React.FC<ViewStudentModalProps> = ({
  open,
  onClose,
  student,
  loading,
  error = null,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Student Details...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!student) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          Student data not available.
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Basic Information
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Student ID" value={student.studentId || "N/A"} />
          <DetailItem
            label="Full Name"
            value={`${student.firstName || ""} ${student.middleName ? `${student.middleName} ` : ""}${student.lastName || ""}`}
          />
          <DetailItem label="Gender" value={student.gender || "N/A"} />
          <DetailItem
            label="Date of Birth"
            value={formatDateForDisplay(student.dateOfBirth)}
          />
          <DetailItem
            label="Nationality"
            value={student.nationality || "N/A"}
          />
          <DetailItem
            label="Blood Group"
            value={formatBloodGroup(student.bloodGroup)}
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Enrollment Information
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Branch" value={student.branchName || "N/A"} />
          <DetailItem
            label="Date of Admission"
            value={formatDateForDisplay(student.dateOfAdmission)}
          />
          <DetailItem
            label="Registered On"
            value={new Date(student.createdAt).toLocaleString()}
          />
          <DetailItem
            label="Last Updated"
            value={new Date(student.updatedAt).toLocaleString()}
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Address Details
        </Typography>

        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          {student.address ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <DetailItem
                label="Street Address"
                value={student.address.streetLine1}
              />
              <DetailItem
                label="Apt / Suite"
                value={student.address.streetLine2 || "N/A"}
              />
              <DetailItem label="City" value={student.address.city} />
              <DetailItem
                label="State / Province"
                value={student.address.stateOrProvince}
              />
              <DetailItem
                label="Postal Code"
                value={student.address.postalCode}
              />
              <DetailItem label="Country" value={student.address.country} />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No address details available.
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="Student Profile" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewStudentModal;
